import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionStatus } from 'thunkless';
import {
  orgBalanceRemainingSelector,
  orgPlanSelector,
  orgAutoFillSelector,
  orgAutoFillThresholdSelector,
  orgAutoFillBalanceSelector,
  orgThresholdOptionsSelector,
  orgBalanceOptionsSelector,
  orgUpdateAutoFillStatusSelector,
  orgIdSelector,
} from '../../../redux/app/selectors/organization';
import { updateAutoFill } from '../../../redux/app/actions/organization';

export const SubscriptionDetailsBalanceSection = () => {
  const dispatch = useDispatch();
  const [isAutoFill, setIsAutoFill] = React.useState(false);
  const [isAutoFillUpdated, setIsAutoFillUpdated] = React.useState(false);
  const [newAutoFillThreshold, setNewAutoFillThreshold] = React.useState(0);
  const [newAutoFillBalance, setNewAutoFillBalance] = React.useState(0);

  const id = useSelector(orgIdSelector);
  const updateAutoFillStatus = useSelector(orgUpdateAutoFillStatusSelector);
  const balanceRemaining = useSelector(orgBalanceRemainingSelector);
  const plan = useSelector(orgPlanSelector);
  const autoFill = useSelector(orgAutoFillSelector);
  const autoFillThreshold = useSelector(orgAutoFillThresholdSelector);
  const autoFillBalance = useSelector(orgAutoFillBalanceSelector);
  const balances = useSelector(orgBalanceOptionsSelector);
  const thresholdOptions = useSelector(orgThresholdOptionsSelector);

  const createBalanceOptions = (el, i) => <option key={new Date().getTime().toString() + i} value={el}>{`$${el.toLocaleString()}.00`}</option>;
  const autoFillThresholdSelectOptions = thresholdOptions.map(createBalanceOptions);
  const balanceSelectOptions = balances.map(createBalanceOptions);

  const isFirstLoadAutoFillStateChanged = (latestAutoFill, latestAFThreshold, latestAFBalance) => {
    const isAutoFillSame = (latestAutoFill !== null ? latestAutoFill : isAutoFill) === autoFill;
    const isAFThresholdSame = (
      latestAFThreshold !== null ? latestAFThreshold : newAutoFillThreshold
    ) === autoFillThreshold;
    const isAFBalanceSame = (
      latestAFBalance !== null ? latestAFBalance : newAutoFillBalance
    ) === autoFillBalance;

    return !isAutoFillSame || !isAFThresholdSame || !isAFBalanceSame;
  };

  const handleUpdateAutoFillSettings = () => {
    dispatch(updateAutoFill({ id, isAutoFill, newAutoFillThreshold, newAutoFillBalance }));
    setIsAutoFillUpdated(false);
  };

  const handleAutoFillChanges = () => {
    setIsAutoFill(!isAutoFill);
    const changed = isFirstLoadAutoFillStateChanged(!isAutoFill, null, null);
    setIsAutoFillUpdated(changed);
  };

  const handleAutoFillThresholdChange = (e) => {
    setNewAutoFillThreshold(parseInt(e.target.value, 10));
    setIsAutoFill(true);

    const changed = isFirstLoadAutoFillStateChanged(true, parseInt(e.target.value, 10), undefined);
    setIsAutoFillUpdated(changed);
  };

  const handleAutoFillBalanceChange = (e) => {
    setNewAutoFillBalance(parseInt(e.target.value, 10));
    setIsAutoFill(true);

    const changed = isFirstLoadAutoFillStateChanged(true, null, parseInt(e.target.value, 10));
    setIsAutoFillUpdated(changed);
  };

  const isTrialPlan = plan === 'trial-plan-4';
  // regex adds commas to long numbers
  const balanceFloat = (balanceRemaining / 10000).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  React.useEffect(() => setIsAutoFill(autoFill), [autoFill]);
  React.useEffect(() => setNewAutoFillThreshold(autoFillThreshold), [autoFillThreshold]);
  React.useEffect(() => setNewAutoFillBalance(autoFillBalance), [autoFillBalance]);

  const isDisabled = (!isAutoFillUpdated && !isAutoFill) || isTrialPlan;

  return (
    <div className="body mt-10">
      <div className="column-left">
        <p className="fsz-16 bold-900">Balance</p>
      </div>
      <div className="column-mid">
        <p className="fsz-16 mb-5 bold-800">{`$${balanceFloat}`}</p>
        <div className="flex mt-15">
          <div
            className={`auto-fill-toggler mr-10 ${isAutoFill ? '' : 'off'} ${isTrialPlan ? 'disabled' : ''}`}
            role="button"
            tabIndex={0}
            onKeyPress={isTrialPlan ? null : handleAutoFillChanges}
            onClick={isTrialPlan ? null : handleAutoFillChanges}
          >
            <div className="assignment-circle circle" />
          </div>
          <div className="flex-col">
            <p>
              Auto-fill:
              &nbsp;
              <span
                className={`bold-800 ${isAutoFill ? 'bright-green' : 'gray'}`}
              >
                {`${isAutoFill ? 'ON' : 'OFF'}`}
              </span>
            </p>
            <p className="fsz-14 mt-5 gray">
              Automatically recharge your account through your default payment
              method anytime your balance runs below the listed amount.
              An email will be sent whenever your account recharges.
            </p>
            <div className="auto-fill-selects-container flex mt-15">
              <div className="balance-threshold flex-col mr-20">
                <p className="bold-800 mb-10">When balance falls below</p>
                <div className={`select-wrapper mr-10 ${isDisabled ? 'disabled' : ''}`}>
                  <select
                    value={newAutoFillThreshold}
                    onChange={handleAutoFillThresholdChange}
                    disabled={isDisabled}
                  >
                    {autoFillThresholdSelectOptions}
                  </select>
                </div>
              </div>
              <div className="flex-col">
                <p className="bold-800 mb-10">Automatically Recharge</p>
                <div className={`select-wrapper mr-10 ${isDisabled ? 'disabled' : ''}`}>
                  <select
                    value={newAutoFillBalance}
                    onChange={handleAutoFillBalanceChange}
                    disabled={isDisabled}
                  >
                    {balanceSelectOptions}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="column-right flex justify-end width-100">
        <button
          type="button"
          className={`button height-fit-content ${isAutoFillUpdated ? 'bright-green' : 'sand'}`}
          onClick={handleUpdateAutoFillSettings}
          disabled={!isAutoFillUpdated || isTrialPlan}
        >
          {updateAutoFillStatus === ActionStatus.BUSY ? 'Updating...' : 'Update Balance'}
        </button>
      </div>
    </div>
  );
};
